import { useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { AlertCircle, CheckCircle, ChevronDown, Copy, FileText, Loader2, Target, TrendingUp } from "lucide-react";

export const BlogGenerator = ({ t, language, userEmail, analysisUrl }) => {
  const [email, setEmail] = useState(userEmail || '');
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [expandedPost, setExpandedPost] = useState(null);
  const [showEmailGate, setShowEmailGate] = useState(!userEmail);
  const [copiedId, setCopiedId] = useState(null);

  const handleGenerate = async () => {
    if (!email || !email.includes('@')) { setShowEmailGate(true); return; }
    setIsLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/blog-generator`, {
        url: analysisUrl || 'https://example.com',
        email, language, count: 10
      });
      setResults(res.data);
    } catch (e) { console.error('Blog gen error:', e); }
    finally { setIsLoading(false); }
  };

  const handleCopy = (post) => {
    navigator.clipboard.writeText(`# ${post.title}\n\n${post.content}\n\n${post.cta || ''}`);
    setCopiedId(post.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const diffColors = { easy: 'bg-green-100 text-green-700', medium: 'bg-orange-100 text-orange-700', hard: 'bg-red-100 text-red-700' };

  return (
    <section className="py-16 px-4 bg-[var(--color-surface)]" id="blog-generator" data-testid="blog-generator-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-900/30 text-purple-400 text-sm font-semibold mb-4">
            <FileText className="w-4 h-4" />
            PRO
          </div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight font-['Chivo'] mb-3">{t.blog_title}</h2>
          <p className="text-[var(--color-text-muted)]">{t.blog_subtitle}</p>
        </div>

        {/* Email Gate */}
        {showEmailGate ? (
          <div className="max-w-md mx-auto p-6 bg-[var(--color-bg)] border-2 border-purple-200 rounded-xl text-center" data-testid="blog-email-gate">
            <FileText className="w-10 h-10 text-purple-500 mx-auto mb-3" />
            <p className="text-sm font-medium text-[var(--color-text)] mb-4">{t.blog_email_required}</p>
            <input type="email" data-testid="blog-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com" className="w-full px-4 py-3 bg-white text-gray-900 placeholder-gray-400 border-2 border-[var(--color-border)] focus:border-purple-500 focus:outline-none mb-3" />
            <button onClick={() => { if(email.includes('@')) setShowEmailGate(false); }} data-testid="blog-email-submit"
              className="w-full px-4 py-3 bg-purple-500 text-white font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50"
              disabled={!email.includes('@')}>
              {t.blog_generate}
            </button>
          </div>
        ) : (
          <>
            {!results && (
              <div className="text-center mb-8">
                <button data-testid="blog-generate-btn" onClick={handleGenerate} disabled={isLoading}
                  className="px-8 py-4 bg-purple-500 text-white font-bold text-lg hover:bg-purple-700 transition-all disabled:opacity-50 flex items-center gap-3 mx-auto rounded-lg">
                  {isLoading ? <><Loader2 className="w-6 h-6 animate-spin" />{t.blog_generating}</> : <><FileText className="w-6 h-6" />{t.blog_generate}</>}
                </button>
              </div>
            )}

            {results && (
              <div className="space-y-8 animate-fade-in" data-testid="blog-results">
                {/* Content Strategy Card */}
                {results.content_strategy && (
                  <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="blog-strategy">
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                      <p className="text-2xl font-mono font-black text-purple-400">{results.content_strategy.total_estimated_monthly_traffic?.toLocaleString()}</p>
                      <p className="text-xs font-medium text-purple-600 mt-1">{t.blog_total_traffic}</p>
                    </div>
                    <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-xl p-4">
                      <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-2">{t.blog_schedule}</p>
                      <p className="text-sm font-medium">{results.content_strategy.posting_schedule}</p>
                    </div>
                    <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-xl p-4">
                      <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-2">{t.blog_topics}</p>
                      <div className="flex flex-wrap gap-1">
                        {(results.content_strategy.primary_topics || []).map((tp, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-900/30 text-purple-400 text-xs rounded">{tp}</span>
                        ))}
                      </div>
                    </div>
                    <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-xl p-4">
                      <p className="text-xs font-semibold text-[var(--color-text-muted)] uppercase mb-2">{t.blog_gaps}</p>
                      <div className="space-y-1">
                        {(results.content_strategy.content_gaps || []).slice(0, 3).map((g, i) => (
                          <p key={i} className="text-xs text-[var(--color-text-muted)] flex items-center gap-1"><AlertCircle className="w-3 h-3 text-orange-500" />{g}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Blog Posts Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  {(results.blog_posts || []).map((post) => (
                    <div key={post.id} className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`blog-post-${post.id}`}>
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <h4 className="font-bold text-sm leading-tight">{post.title}</h4>
                          <span className={`px-2 py-0.5 text-xs font-bold rounded flex-shrink-0 ${diffColors[post.difficulty] || 'bg-[var(--color-surface-alt)]'}`}>{post.difficulty}</span>
                        </div>
                        <p className="text-xs text-[var(--color-text-muted)] mb-3">{post.meta_description}</p>
                        <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)] mb-3">
                          <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />{post.estimated_traffic} {t.blog_traffic}</span>
                          <span className="flex items-center gap-1"><Target className="w-3 h-3" />{post.target_keyword}</span>
                          <span>{post.word_count} words</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                            className="flex-1 px-3 py-2 border border-[var(--color-border)] text-xs font-semibold text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] transition-colors flex items-center justify-center gap-1">
                            <ChevronDown className={`w-3 h-3 transition-transform ${expandedPost === post.id ? 'rotate-180' : ''}`} />
                            {expandedPost === post.id ? 'Close' : t.blog_edit}
                          </button>
                          <button onClick={() => handleCopy(post)} data-testid={`copy-blog-${post.id}`}
                            className="px-3 py-2 bg-purple-500 text-white text-xs font-semibold hover:bg-purple-700 transition-colors flex items-center gap-1">
                            {copiedId === post.id ? <CheckCircle className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copiedId === post.id ? 'Copied!' : t.blog_copy}
                          </button>
                        </div>
                      </div>
                      {expandedPost === post.id && (
                        <div className="border-t border-[var(--color-border)] p-5 bg-[var(--color-bg)] animate-fade-in">
                          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-[var(--color-text)] text-xs leading-relaxed" data-testid={`blog-content-${post.id}`}>
                            {post.content}
                          </div>
                          {post.cta && <p className="mt-4 text-xs font-semibold text-purple-600 italic">{post.cta}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};


// SEO Tools Section Component
